import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Chip, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import ReportFilters from "./report/components/ReportFilters";
import ReportKpiCards from "./report/components/ReportKpiCards";
import ReportCharts from "./report/components/ReportCharts";
import ReportDataGrid from "./report/components/ReportDataGrid";
import AppraisalDetailsDrawer from "./report/components/AppraisalDetailsDrawer";
import FinalizeActionDialog from "./report/components/FinalizeActionDialog";
import ReportErrorBoundary from "./report/components/ReportErrorBoundary";
import { useAppraisalsReport } from "./report/useAppraisalsReport";
import {
  buildExportRows,
  DEFAULT_FILTERS,
  exportRowsToCsv,
  exportRowsToExcel,
  filterItemsClient,
  isFinalizeAllowed,
  normalizeReportResponse,
  safeMsg,
} from "./report/report.utils";
import { fetchAppraisalsReport } from "../../services/performanceReportApi";
import { PageHeader, PageLayout } from "../../components/ui";

const INITIAL_PAGINATION = { page: 0, pageSize: 25 };
const INITIAL_SORT = [{ field: "last_updated_at", sort: "desc" }];
const createEmptySelectionModel = () => ({ type: "include", ids: new Set() });

function AppraisalsReportPageContent() {
  const theme = useTheme();
  const role = String(localStorage.getItem("role") || "employee").toLowerCase();
  const canFinalize = isFinalizeAllowed(role);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState(INITIAL_PAGINATION);
  const [sortModel, setSortModel] = useState(INITIAL_SORT);
  const [rowSelectionModel, setRowSelectionModel] = useState(createEmptySelectionModel);
  const [activeQuickFilter, setActiveQuickFilter] = useState("");

  const [detailRow, setDetailRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeItems, setFinalizeItems] = useState([]);
  const [exporting, setExporting] = useState(false);

  const {
    queryParams,
    rows,
    allItems,
    rowCount,
    summary,
    kpis,
    statusDistribution,
    lookups,
    isLoading,
    isFetching,
    isError,
    errorMessage,
    refetch,
    finalizeActions,
    finalizePending,
    finalizeError,
    legacySource,
  } = useAppraisalsReport({ filters, search, paginationModel, sortModel });

  const selectedIds = useMemo(() => {
    if (Array.isArray(rowSelectionModel)) return rowSelectionModel;
    if (rowSelectionModel?.ids instanceof Set) return Array.from(rowSelectionModel.ids);
    if (Array.isArray(rowSelectionModel?.ids)) return rowSelectionModel.ids;
    return [];
  }, [rowSelectionModel]);

  const selectedItems = useMemo(() => {
    const map = new Map();
    [...allItems, ...rows].forEach((item) => {
      if (!item || item.id === undefined || item.id === null) return;
      map.set(String(item.id), item);
    });

    return selectedIds
      .map((id) => map.get(String(id)))
      .filter(Boolean);
  }, [selectedIds, allItems, rows]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setRowSelectionModel(createEmptySelectionModel());
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearch("");
    setPaginationModel(INITIAL_PAGINATION);
    setSortModel(INITIAL_SORT);
    setRowSelectionModel(createEmptySelectionModel());
    setActiveQuickFilter("");
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleKpiQuickFilter = useCallback(
    (key) => {
      setActiveQuickFilter((prev) => (prev === key ? "" : key));

      if (key === "total") {
        setFilters((prev) => ({
          ...prev,
          status: "",
          recommendation_status: "",
          delta_direction: "",
        }));
        return;
      }

      if (key === "completed") {
        updateFilter("status", filters.status === "completed" ? "" : "completed");
        return;
      }

      if (key === "proposed") {
        updateFilter(
          "recommendation_status",
          filters.recommendation_status === "proposed" ? "" : "proposed"
        );
        return;
      }

      if (key === "approved") {
        updateFilter(
          "recommendation_status",
          filters.recommendation_status === "approved" ? "" : "approved"
        );
        return;
      }

      if (key === "avg_delta") {
        updateFilter("delta_direction", filters.delta_direction === "improved" ? "" : "improved");
      }
    },
    [filters.status, filters.recommendation_status, filters.delta_direction, updateFilter]
  );

  const handleBandChartClick = useCallback(
    (band) => updateFilter("band", filters.band === band ? "" : band),
    [filters.band, updateFilter]
  );

  const handleActionChartClick = useCallback(
    (action) =>
      updateFilter("recommended_action", filters.recommended_action === action ? "" : action),
    [filters.recommended_action, updateFilter]
  );

  const handleStatusChartClick = useCallback(
    (status) => updateFilter("status", filters.status === status ? "" : status),
    [filters.status, updateFilter]
  );

  const openDetails = useCallback((row) => {
    setDetailRow(row);
    setDetailsOpen(true);
  }, []);

  const openFinalizeSingle = useCallback(
    (row) => {
      if (!canFinalize) return;
      setFinalizeItems([row]);
      setFinalizeOpen(true);
    },
    [canFinalize]
  );

  const openFinalizeBulk = useCallback(() => {
    if (!canFinalize) return;
    if (!selectedItems.length) {
      toast.warn("Select at least one appraisal to finalize.");
      return;
    }
    setFinalizeItems(selectedItems);
    setFinalizeOpen(true);
  }, [canFinalize, selectedItems]);

  const handleFinalizeSubmit = useCallback(
    async (payload) => {
      await finalizeActions(payload);
      toast.success("Final action applied successfully.");
      setFinalizeOpen(false);
      setRowSelectionModel(createEmptySelectionModel());
    },
    [finalizeActions]
  );

  const runExport = useCallback(
    async (kind) => {
      try {
        setExporting(true);

        const exportPayload = await fetchAppraisalsReport({
          ...queryParams,
          page: 1,
          per_page: 5000,
        });

        const normalized = normalizeReportResponse(exportPayload || {});
        const exportItems = filterItemsClient(normalized.items, filters, search);
        const rowsForExport = buildExportRows(exportItems);

        if (!rowsForExport.length) {
          toast.info("No rows available for export.");
          return;
        }

        if (kind === "csv") exportRowsToCsv(rowsForExport, "appraisals_report");
        else exportRowsToExcel(rowsForExport, "appraisals_report");

        toast.success(`Exported ${rowsForExport.length} rows.`);
      } catch (error) {
        toast.error(safeMsg(error, "Failed to export report."));
      } finally {
        setExporting(false);
      }
    },
    [queryParams, filters, search]
  );

  const activeFiltersCount = useMemo(
    () =>
      Object.values(filters || {}).filter(
        (v) => v !== undefined && v !== null && String(v).trim() !== ""
      ).length + (String(search || "").trim() ? 1 : 0),
    [filters, search]
  );

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        overline="Performance analytics"
        title="Appraisals report"
        subtitle="Score distribution, historical deltas, recommendations, and final action tracking."
        actions={[
          <Button
            key="refresh"
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={isLoading || isFetching}
            sx={{ borderRadius: "8px" }}
          >
            Refresh
          </Button>,
        ]}
      />

      <Stack spacing={1.1}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1, md: 1.2 },
            borderRadius: "8px",
            borderColor: theme.hr?.line?.soft || alpha(theme.palette.primary.main, 0.14),
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "none",
          }}
        >
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip size="small" label={`Total ${kpis.total || 0}`} color="primary" sx={{ borderRadius: "8px" }} />
            <Chip size="small" variant="outlined" label={`Role ${role}`} sx={{ borderRadius: "8px" }} />
            <Chip
              size="small"
              variant="outlined"
              label={`Selected ${selectedItems.length}`}
              color={selectedItems.length > 0 ? "success" : "default"}
              sx={{ borderRadius: "8px" }}
            />
            <Chip
              size="small"
              color={activeFiltersCount > 0 ? "warning" : "default"}
              variant="outlined"
              label={`Filters ${activeFiltersCount}`}
              sx={{ borderRadius: "8px" }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: { md: "auto" } }}>
              Data grid is server paginated; exports use the current filter scope.
            </Typography>
          </Stack>
        </Paper>

        <ReportFilters
          filters={filters}
          search={search}
          lookups={lookups}
          onFilterChange={updateFilter}
          onSearchChange={handleSearchChange}
          onReset={handleResetFilters}
          onExportCsv={() => runExport("csv")}
          onExportExcel={() => runExport("excel")}
          onFinalize={openFinalizeBulk}
          canFinalize={canFinalize}
          finalizeDisabled={!selectedItems.length || finalizePending || exporting}
          selectedCount={selectedItems.length}
        />

        {legacySource?.used ? (
          <Alert severity="info">
            Fallback report source is active. If backend `/performance/appraisals/report` is unavailable,
            recommendation and change fields may be partially populated.
          </Alert>
        ) : null}

        <ReportKpiCards
          kpis={kpis}
          activeQuickFilter={activeQuickFilter}
          onQuickFilter={handleKpiQuickFilter}
        />

        <ReportCharts
          summary={summary}
          statusDistribution={statusDistribution}
          activeBand={filters.band}
          activeAction={filters.recommended_action}
          activeStatus={filters.status}
          onBandClick={handleBandChartClick}
          onActionClick={handleActionChartClick}
          onStatusClick={handleStatusChartClick}
        />

        {isFetching ? <LinearProgress /> : null}

        {isError && !rows.length ? (
          <Alert severity="error" action={null}>
            {errorMessage}
          </Alert>
        ) : null}

        {!isLoading && !rows.length && !isError ? (
          <Alert severity="info">
            No appraisals match the current filters.
            <Typography component="span" sx={{ ml: 0.5 }}>
              Try adjusting date range, status, band, or recommendation filters.
            </Typography>
          </Alert>
        ) : null}

        <ReportDataGrid
          rows={rows}
          rowCount={rowCount}
          loading={isLoading || isFetching}
          errorMessage={isError ? errorMessage : ""}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          selectionModel={rowSelectionModel}
          onSelectionModelChange={setRowSelectionModel}
          search={search}
          onSearchChange={handleSearchChange}
          onRetry={refetch}
          canFinalize={canFinalize}
          onOpenDetails={openDetails}
          onOpenFinalize={openFinalizeSingle}
        />
      </Stack>

      <AppraisalDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        item={detailRow}
      />

      <FinalizeActionDialog
        open={finalizeOpen}
        items={finalizeItems}
        positions={lookups?.positions || []}
        submitting={finalizePending}
        submitError={finalizeError ? safeMsg(finalizeError, "Failed to finalize action.") : ""}
        onClose={() => setFinalizeOpen(false)}
        onSubmit={handleFinalizeSubmit}
      />
    </PageLayout>
  );
}

export default function AppraisalsReportPage() {
  return (
    <ReportErrorBoundary>
      <AppraisalsReportPageContent />
    </ReportErrorBoundary>
  );
}
